/* eslint-disable max-lines */
import { Component, Input, NgZone, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { endOfDay, startOfDay } from 'date-fns';
import { AccountAdminFormResultMap, CompanyServices } from '../../models/account-admin-search';
import { AccountAdminService } from '../../services/account-admin/account-admin.service';
import { TranslateService } from '@ngx-translate/core';
import { Option } from 'common/components/select/select.component';
import { environment } from 'common/environments/environment';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { IdToken } from 'common/models';
import { OktaService } from 'common/services/okta/okta.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { RelationshipCriteriaDate } from 'private/app/models/account-admin-company.model';
import endOfYear from 'date-fns/endOfYear';
import { map, mergeMap } from 'rxjs/operators';
import { AccountPostAdminService } from 'private/app/services/account-admin/account-post-admin.service';
import { Select, Store } from '@ngxs/store';
import { ApprovalRequestsState } from 'private/app/store/approval-requests.state';
import { FetchApprovalRequests } from 'private/app/store/approval-requests.actions';
import { AccountAdminDistributorBranch } from 'private/app/models/account-admin-search-results';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { ToastService } from 'common/services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CompanyCategoryType } from 'private/app/views/account-admin-company-details/company-query/company-query-configuration';
import { Values } from 'private/app/services/account-admin/account-admin-form-group-config';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { OptGroupOptions } from 'private/app/models/distributor-relationships.model';
import { Router } from '@angular/router';

export interface AllCountries{
    name: string
    value: string | null
}
@Component({
    selector: 'hvac-account-admin-form',
    templateUrl: './account-admin-form.component.html',
    styleUrls: ['./account-admin-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { '[class.hvac-account-admin-form--results]': 'onAccountAdminResult' }
})
export class AccountAdminFormComponent implements OnInit {
    @Input() onAccountAdminResult: Boolean;
    @Select(ApprovalRequestsState.requestCount) requestCount$: Observable<number>;
    approvalRequestsNotification$: Observable<string>;
    public readonly todayDate: number = new Date().getTime();
    public readonly endOfYearDate: number = endOfYear(this.todayDate).getTime();
    date$: BehaviorSubject<RelationshipCriteriaDate>;

    dateError$: BehaviorSubject<{ dateType: string, error: boolean }> = new BehaviorSubject<{ dateType: string, error: boolean }>({
        dateType: '',
        error: false
    });

    public startDate: number | undefined;
    public endDate: number | undefined;
    public companyStartDate: number | undefined;
    public companyEndDate: number | undefined;
    public userStartDate: number | undefined;
    public userEndDate: number | undefined;

    dateErrorMessage$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public idmUserRole: string[];
    public isRegAdmin: boolean = false;
    public formData?: AccountAdminFormResultMap;
    public showDealerSearchCriteriaCIN = environment.features.dealerSearchCriteriaCIN;
    public showRelationshipType = environment.features.relationshipType;
    public isLegealEntitySelected: boolean = false;
    public legalEntityOptionData$ = new BehaviorSubject<Option[]>([]);
    public distributorBranchOptionData$ = new BehaviorSubject<Option[]>([]);
    public companyTypes: Option[];
    public optGroupBrands?: OptGroupOptions[];
    public companyServices: Option[] = [];
    public isAccountAdmin: boolean;
    public isUserAdmin: boolean;
    public isCompanyAdmin: boolean;
    public isNonInternalDistributor$ = new BehaviorSubject<boolean>(false);
    isAccordionOpen: boolean;
    isSubmitDisabled: boolean = true;
    public countryList$: Observable<Option[]>;
    isResetDateRangePicker: boolean = false;
    showDateRangePicker: boolean = false;
    showCompanyDateRangePicker: boolean = false;
    showUserDateRangePicker: boolean = false;
    showActiveOnlyMessage: boolean = false;
    showBrandLimitError: boolean = false;
    readonly maxBrandSelection = 7;
    public companyRelationDateControls: [FormControl, FormControl];
    public companyDateControls: [FormControl, FormControl];
    public userDateControls: [FormControl, FormControl];
    public companyCategory : string = '';
    public isInternalUser : boolean = false;
    public isDistributor : boolean = false;
    public selectID: string;
    public locatorBrands: Option[] = [];
    private previousBrandValue: Option[] | string = '';
    private isRevertingBrandSelection = false;

    constructor(
        public accountAdminService: AccountAdminService,
        public commonUtils: CommonUtilsService,
        public accountPostAdminService: AccountPostAdminService,
        public accountAdminApiService: AccountAdminApiService,
        private router: Router,
        private translate: TranslateService,
        private ngZone: NgZone,
        private oktaService: OktaService,
        private readonly store: Store,
        private toastService: ToastService
    ) {}

    @HostListener('document:keydown.enter', ['$event'])
    onEnterPress() {
        !this.isSubmitDisabled && this.onSubmit(this.accountAdminService.advancedSearchForm);
    }

    ngOnInit(): void {
        this.oktaService.idToken$.subscribe((res: IdToken) => {
            this.idmUserRole = res?.claims?.idm_user_roles || [];
            this.companyCategory = res?.claims?.idm_user_companies[0]?.companyCategory?.name;
            this.isInternalUser = this.companyCategory === this.translate.instant('ACCOUNT_ADMIN.INTERNAL');
            this.isDistributor = this.companyCategory === CompanyCategoryType.DOMESTIC_DISTRIBUTOR;
            this.isNonInternalDistributor$.next(this.isNonInternalDistributor(this.idmUserRole));
            if (this.isNonInternalDistributor$.value) {
                this.getDistributorBranches(res?.claims?.idm_user_companies[0]?.id);
            }
            if (this.idmUserRole.includes(IdmUserRoles.ACCOUNTADMIN)) {
                this.isAccountAdmin = true;
            }
            if (this.idmUserRole.includes(IdmUserRoles.REG_ADMIN)) {
                this.isRegAdmin = true;
            }
            if (this.idmUserRole.includes(IdmUserRoles.USERADMIN)) {
                this.isUserAdmin = true;
            }
            if (this.idmUserRole.includes(IdmUserRoles.COMPANYADMIN)) {
                this.isCompanyAdmin = true;
            }
        });
        this.accountAdminService.getAccountAdminSearchForm().subscribe((data: AccountAdminFormResultMap) => {
            this.optGroupBrands = this.commonUtils.transformBrandsToGroups(data.brands);
            this.legalEntityOptionData$.next(data.legalEntity);
            this.companyTypes = data.companyTypes.map(({ companyTypes }) => companyTypes).flat().map((companyType) => ({
                name: companyType?.name!,
                value: companyType?.code!
            })).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            this.companyTypes.unshift({
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_COMPANY_TYPE'),
                value: ''
            });
            this.formData = data;
        }, (error: HttpErrorResponse) => this.toastService.raiseDefaultErrorToast('AccountAdminFormComponent-getAccountAdminSearchForm', error.message));

        this.countryList$ = this.commonUtils.getCountryOptions().pipe(
            map((countries) => this.commonUtils.reprioritizeCountries(countries))
        );

        this.accountAdminService.getCompanyServices().subscribe((services) => {
            this.companyServices = [{
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_COMPANY_SERVICE'),
                value: ''
            }];
            services.forEach((service: CompanyServices) => {
                this.companyServices.push({
                    name: `${service.name}-${service.code}`,
                    value: service.id
                });
            });
        });

        this.accountAdminApiService.getAccountAdminCompanyDealerLocatorBrandsAll().subscribe((result) => {
            this.locatorBrands = [{
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_LOCATOR'),
                value: ''
            }];
            result.map((res, i) => this.locatorBrands.push({
                name: res,
                value: `${i}`
            }));
        });

        this.isAccordionOpen = false;
        this.store.dispatch(new FetchApprovalRequests());
        this.approvalRequestsNotification$ = this.requestCount$.pipe(
            mergeMap((count) => this.translate.get('ACCOUNT_ADMIN.REGISTRATION_REQUEST', { count }))
        );
        this.companyRelationDateControls = [this.accountAdminService.advancedSearchForm.controls.startDate, this.accountAdminService.advancedSearchForm.controls.endDate];
        this.companyDateControls = [this.accountAdminService.advancedSearchForm.controls.companyStartDate, this.accountAdminService.advancedSearchForm.controls.companyEndDate];
        this.userDateControls = [this.accountAdminService.advancedSearchForm.controls.userStartDate, this.accountAdminService.advancedSearchForm.controls.userEndDate];

        Object.keys(this.accountAdminService.advancedSearchForm.controls).forEach((field) => {
            if (this.commonUtils.dropDownsList.includes(field)) {
                this.onDropDownChanges(field);
            }
        });

        this.previousBrandValue = this.accountAdminService.advancedSearchForm.controls.brand.value;
        this.accountAdminService.advancedSearchForm.controls.brand.valueChanges.subscribe((value: Option[] | string) => {
            this.onBrandSelectionChange(value);
        });

        this.accountAdminService.advancedSearchForm.valueChanges.subscribe((values) => {
            this.isSubmitDisabled = this.checkIfFormIsEmpty(values);
        });
        this.isSubmitDisabled = this.checkIfFormIsEmpty(this.accountAdminService.advancedSearchForm.value);
    }

    onDropDownChanges(field: string) {
        const control = this.accountAdminService.advancedSearchForm.get(field) as FormControl;
        control.valueChanges.subscribe((value) => {
            if (value[0]?.value) {
                this.commonUtils.disabledDropdowns = this.commonUtils.updateDropdownState(field as keyof Values);
                this.commonUtils.dropDownsList.forEach((key) => ((key === field) ? '' : this.accountAdminService.advancedSearchForm.get(key)?.disable()));
            }
            else if (value[0]?.value === '') {
                this.commonUtils.disabledDropdowns = this.commonUtils.enableAllDropdowns();
                this.commonUtils.dropDownsList.forEach((key) => {
                    this.accountAdminService.advancedSearchForm.get(key)?.reset('');
                    this.accountAdminService.advancedSearchForm.get(key)?.enable();
                });
            }
        });
    }

    handleToggle() {
        this.isAccordionOpen = !this.isAccordionOpen;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkIfFormIsEmpty(values: any): boolean {
    /* eslint-disable no-undefined */
        const hasCompanyDateCriteria = values.companyDates?.[0]?.value;
        const hasCreatedDateCriteria = values.createdDate?.[0]?.value;
        const hasUserDateCriteria = values.userDates?.[0]?.value;
        if (hasCompanyDateCriteria && (!values.companyStartDate || !values.companyEndDate)) {
            return true;
        }
        if (hasCreatedDateCriteria && (!values.startDate || !values.endDate)) {
            return true;
        }
        if (hasUserDateCriteria && (!values.userStartDate || !values.userEndDate)) {
            return true;
        }

        const hasAnyNonStatusCriteria = Object.entries(values as Record<string, string>).some(([key, value]) => {
            if (key === 'statusActive') {
                return false;
            }

            if (typeof value === 'boolean') {
                return value;
            }

            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return false;
                }

                return value[0]?.value !== '';
            }

            return value !== null && value !== '' && value !== undefined;
        });
        if (values.statusActive === true && !hasAnyNonStatusCriteria) {
            this.showActiveOnlyMessage = true;

            return true;
        }
        this.showActiveOnlyMessage = false;

        return !hasAnyNonStatusCriteria;
    }

    public onSubmit(event: UntypedFormGroup) {
        this.accountAdminService.lastQueryParams.next(this.accountAdminService.createQueryParams(event));


        this.ngZone.run(() => {
            this.router.navigate([`/Admin/Admin-Tools/account-admin.html/results/${this.accountAdminService.currentTab}`], { queryParams: this.accountAdminService.lastQueryParams.value });
        });
    }

    setRelationshipCriteriaDate(dates: RelationshipCriteriaDate) {
        this.isResetDateRangePicker = false;
        if (dates.startDate) {
            this.startDate = startOfDay(dates.startDate).getTime();
        }
        if (dates.endDate) {
            this.endDate = endOfDay(dates.endDate).getTime();
        }
    }

    setCompanyCriteriaDate(dates: RelationshipCriteriaDate) {
        this.isResetDateRangePicker = false;
        if (dates.startDate) {
            this.companyStartDate = startOfDay(dates.startDate).getTime();
        }
        if (dates.endDate) {
            this.companyEndDate = endOfDay(dates.endDate).getTime();
        }
    }

    setUserCriteriaDate(dates: RelationshipCriteriaDate) {
        this.isResetDateRangePicker = false;
        if (dates.startDate) {
            this.userStartDate = startOfDay(dates.startDate).getTime();
        }
        if (dates.endDate) {
            this.userEndDate = endOfDay(dates.endDate).getTime();
        }
    }

    clearAll() {
    /* eslint-disable no-undefined */
        this.startDate = undefined;
        this.endDate = undefined;
        this.isResetDateRangePicker = true;
        this.showBrandLimitError = false;
        this.commonUtils.disabledDropdowns = this.commonUtils.enableAllDropdowns();
        this.commonUtils.dropDownsList.forEach((key) => {
            this.accountAdminService.advancedSearchForm.get(key)?.reset('');
            this.accountAdminService.advancedSearchForm.get(key)?.enable();
        });
        this.accountAdminService.advancedSearchForm.reset(this.accountAdminService.advancedSearchFormDefault.value);
        this.previousBrandValue = this.accountAdminService.advancedSearchForm.controls.brand.value;
    }

    isNonInternalDistributor(idmUserRoles: string[]) {
        return !idmUserRoles?.includes(IdmUserRoles.DEFAULT_INTERNAL) && (idmUserRoles?.includes(IdmUserRoles.COMPANY_TYPE_INTERNATIONAL_DISTRIBUTOR) || idmUserRoles?.includes(IdmUserRoles.COMPANY_TYPE_DOMESTIC_DISTRIBUTOR) || idmUserRoles?.includes(IdmUserRoles.COMPANY_TYPE_MARINE_DISTRIBUTOR));
    }

    onCreatedDateChange(event: Option[]) {
        this.showDateRangePicker = Boolean(event[0]?.value);
        if (!event[0]?.value) {
            this.startDate = undefined;
            this.endDate = undefined;
            this.setRelationshipCriteriaDate({
                startDate: undefined,
                endDate: undefined
            });
        }
    }

    onCompanyDateChange(event: Option[]) {
        this.showCompanyDateRangePicker = Boolean(event[0]?.value);
        if (!event[0]?.value) {
            this.companyStartDate = undefined;
            this.companyEndDate = undefined;
            this.setCompanyCriteriaDate({
                startDate: undefined,
                endDate: undefined
            });
        }
    }

    onUserDateChange(event: Option[]) {
        this.showUserDateRangePicker = Boolean(event[0]?.value);
        if (!event[0]?.value) {
            this.userStartDate = undefined;
            this.userEndDate = undefined;
            this.setUserCriteriaDate({
                startDate: undefined,
                endDate: undefined
            });
        }
    }

    onLegalEntityChange(event: Option[]) {
        if (!this.isNonInternalDistributor$.value) {
            this.selectID = event[0]?.value;
            if (event[0]?.value !== this.accountAdminService.selectedLegalEntityValue$.value) {
                this.accountAdminService.advancedSearchForm.controls.distributorBranch.setValue('');
            }
            this.distributorBranchOptionData$.next([]);
            if (event[0]?.value) {
                this.isLegealEntitySelected = true;
                this.accountAdminService.selectedLegalEntityValue$.next(event[0].value);
                this.getDistributorBranches(event[0].value);
            }
            else {
                this.isLegealEntitySelected = false;
            }
        }
    }

    getDistributorBranches(selectedLegalEntityId: string) {
        this.accountPostAdminService.getDistributorBranch(selectedLegalEntityId).subscribe((branches) => {
            this.distributorBranchOptionData$.next(this.mapDistributorBranches(branches));
        });
    }

    mapDistributorBranches(distBranches: AccountAdminDistributorBranch[]): Option[] {
        return [
            {
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_DISTRIBUTOR_BRANCH'),
                value: ''
            },
            ...distBranches.map((branch: AccountAdminDistributorBranch) => ({
                name: branch.companyName,
                value: branch.hvacCompanyId
            })).slice().sort((valueA, valueB) => valueA.name.toLowerCase().localeCompare(valueB.name.toLowerCase()))
        ];
    }

    private getSelectedBrandCount(value: Option[] | string): number {
        if (!Array.isArray(value)) {
            return 0;
        }

        return value.filter((item: Option) => item?.value).length;
    }

    private onBrandSelectionChange(value: Option[] | string): void {
        if (this.isRevertingBrandSelection) {
            this.isRevertingBrandSelection = false;

            return;
        }

        if (this.getSelectedBrandCount(value) > this.maxBrandSelection) {
            this.showBrandLimitError = true;
            this.isRevertingBrandSelection = true;
            const previousCount = this.getSelectedBrandCount(this.previousBrandValue);
            let revertValue: Option[] | string = '';
            if (previousCount > 0 && previousCount <= this.maxBrandSelection) {
                revertValue = this.previousBrandValue;
            }
            else if (Array.isArray(value)) {
                revertValue = value.filter((item: Option) => item?.value).slice(0, this.maxBrandSelection);
            }
            this.previousBrandValue = revertValue;
            this.accountAdminService.advancedSearchForm.controls.brand.setValue(revertValue);

            return;
        }

        this.showBrandLimitError = false;
        this.previousBrandValue = value;
    }
}
